import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Typography,
  notification,
  Tooltip,
  TablePaginationConfig,
  Space,
  Tag,
  Empty,
  Button,
  TableColumnsType,
} from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import moment from "moment";
import { ApplicationAction } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { usePermissions } from "../../PermissionsProvider";
import TableDropDownMenu from "../shared/tablemenus/TableDropdownMenu";
import { IntentService } from "../../services/IntentService";

interface IntentsTableProps {
  applicationId?: number; // For application-specific intents
  intentGroupId?: number; // For intent group intents
  showApplicationColumn?: boolean; // Whether to show the application column
  emptyText?: React.ReactNode;
  styles?: Record<string, any>; // Pass through styles
}

const IntentsTable: React.FC<IntentsTableProps> = ({
  applicationId,
  intentGroupId,
  showApplicationColumn = true,
  emptyText,
  styles = {},
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [intents, setIntents] = useState<ApplicationAction[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const deleteIntent = (actionId: number, appId: number) => {
    if (window.confirm("Are you sure you want to delete this intent?")) {
      ApplicationService.deleteApplicationAction(
        appId,
        actionId,
        setIsLoading,
        apiKey,
      ).subscribe(
        () => {
          loadIntents();
          notification.success({
            message: "Success",
            description: "Intent deleted successfully",
          });
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to delete intent",
          });
        },
      );
    }
  };

  const loadIntents = () => {
    setIsLoading(true);
    
    const offset = (currentPage - 1) * pageSize;
    
    if (applicationId) {
      // Load application-specific intents
      ApplicationService.getApplicationActions(
        applicationId,
        setIsLoading,
        apiKey,
      ).subscribe(
        (actions: ApplicationAction[]) => {
          // Sort actions by last_activity in descending order
          const sortedActions = actions.sort((a, b) => {
            const dateA = a.last_activity > 0 ? a.last_activity : 0;
            const dateB = b.last_activity > 0 ? b.last_activity : 0;
            return dateB - dateA;
          });
          setIntents(sortedActions);
          setTotal(sortedActions.length);
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        },
      );
    } else if (intentGroupId) {
      // Load intent group intents
      IntentService.getIntentsByIntentGroup(
        intentGroupId,
        offset,
        pageSize,
        setIsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          setIntents(data.items || []);
          setTotal(data.total || 0);
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
          notification.error({
            message: "Error",
            description: "Failed to fetch intents",
          });
        },
      );
    }
  };

  useEffect(() => {
    loadIntents();
  }, [applicationId, intentGroupId, currentPage, apiKey]);

  const columns: TableColumnsType<ApplicationAction> = [
    ...(showApplicationColumn
      ? [
          {
            title: "Application",
            dataIndex: ["meta", "metadata", "application_name"],
            key: "application_name",
            width: "20%",
            render: (text: string, record: ApplicationAction) => (
              <Link to={`/applications/${record.application_id}`} style={{ fontWeight: 500, color: "#1890ff" }}>
                {text}
              </Link>
            ),
          },
        ]
      : []),
    {
      title: "Intent",
      dataIndex: "action_type",
      key: "action_type",
      width: "25%",
      render: (text: string, record: ApplicationAction) => (
        <Link to={`/application/${record.application_id}/intent/${record.id}`} style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Link>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity",
      key: "last_activity",
      width: "20%",
      render: (timestamp: number) => (
        <Tooltip title={moment(timestamp).format("MMMM D, YYYY h:mm:ss A")}>
          {timestamp > 0 ? moment(timestamp).fromNow() : "Never"}
        </Tooltip>
      ),
    },
    {
      title: "Requests",
      dataIndex: "request_count",
      key: "request_count",
      width: "10%",
      render: (count: number) => (
        <Typography.Text strong style={{ color: count > 0 ? "#52c41a" : "#8c8c8c" }}>
          {count}
        </Typography.Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "5%",
      render: (_: any, record: ApplicationAction) => {
        const items = [
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/application/${record.application_id}/intent/${record.id}`);
                }}>
                Edit
              </a>
            ),
            key: "0",
            icon: <EditOutlined />,
          },
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteIntent(record.id, record.application_id);
                }}>
                Delete
              </a>
            ),
            key: "1",
            icon: <DeleteOutlined />,
          },
        ];
        return <TableDropDownMenu items={items} />;
      },
      align: "right" as const,
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
  };

  return (
    <div style={styles.tableContainer}>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={intents}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          onChange: (page) => setCurrentPage(page),
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => navigate(`/application/${record.application_id}/intent/${record.id}`),
          style: {
            cursor: "pointer",
            transition: "background-color 0.2s ease",
          }
        })}
        rowClassName={() => "interactive-row"}
        locale={{
          emptyText: emptyText || (
            <Empty 
              description={
                <div>
                  <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                    No intents found
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    No intents have been created yet.
                  </Typography.Text>
                </div>
              } 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
            />
          )
        }}
      />
    </div>
  );
};

export default IntentsTable; 